import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import { useParams, useNavigate } from "react-router-dom";
import { 
  FaArrowLeft, FaHistory, FaCalendarAlt, FaUser, 
  FaEnvelope, FaPhoneAlt, FaSchool, FaCheckCircle, 
  FaTimesCircle, FaShoppingBag, FaFileInvoice
} from "react-icons/fa";

function ParentOrderDetails() {
  const { parentId } = useParams();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(true);

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatAmount = (value) => Number(value || 0).toFixed(2);

  const numberToWords = (amount) => {
    const num = Math.floor(Number(amount || 0));
    if (num === 0) return "Zero Only";

    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const belowHundred = (n) => {
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      return `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${ones[n % 10]}` : ""}`;
    };

    const belowThousand = (n) => {
      if (n < 100) return belowHundred(n);
      return `${ones[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${belowHundred(n % 100)}` : ""}`;
    };

    const parts = [];
    let remaining = num;

    const crore = Math.floor(remaining / 10000000);
    if (crore) {
      parts.push(`${belowThousand(crore)} Crore`);
      remaining %= 10000000;
    }

    const lakh = Math.floor(remaining / 100000);
    if (lakh) {
      parts.push(`${belowThousand(lakh)} Lakh`);
      remaining %= 100000;
    }

    const thousand = Math.floor(remaining / 1000);
    if (thousand) {
      parts.push(`${belowThousand(thousand)} Thousand`);
      remaining %= 1000;
    }

    if (remaining) parts.push(belowThousand(remaining));
    return `${parts.join(" ")} Only`;
  };

  const handleInvoice = (order) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const customerName = order.address?.name || order.parentName || parent?.name || "Valued Customer";
    const customerPhone = order.address?.phone || parent?.mobile || "N/A";
    const totalAmount = order.totalAmount || 0;
    const taxableAmount = totalAmount;
    const netAmount = totalAmount;
    const orderDate = new Date(order.createdAt || order.orderDate || Date.now()).toLocaleDateString("en-IN");
    const orderNumber = String(order._id || "").slice(-8).toUpperCase();

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice #${escapeHtml(orderNumber)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #000; line-height: 1.4; margin: 0; }
            .invoice-header { text-align: center; margin-bottom: 20px; }
            .company-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
            .company-subtitle { font-size: 14px; color: #666; }
            .invoice-title { font-size: 20px; font-weight: bold; margin: 20px 0 10px 0; text-align: center; }
            .invoice-details { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .detail-col { width: 48%; }
            .detail-row { display: flex; margin-bottom: 5px; }
            .detail-label { width: 120px; font-weight: bold; font-size: 12px; }
            .detail-value { flex: 1; font-size: 12px; }
            .address-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .address-col { width: 48%; border: 1px solid #ccc; padding: 10px; }
            .address-title { font-weight: bold; margin-bottom: 10px; font-size: 14px; }
            .address-content { font-size: 12px; line-height: 1.3; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 12px; }
            th { border: 1px solid #000; padding: 5px; text-align: center; background: #f5f5f5; font-weight: bold; }
            td { border: 1px solid #000; padding: 5px; text-align: center; }
            .text-left { text-align: left; }
            .text-right { text-align: right; }
            .bank-details { margin-top: 20px; }
            .bank-title { font-weight: bold; margin-bottom: 10px; font-size: 14px; }
            .bank-row { display: flex; margin-bottom: 5px; font-size: 12px; }
            .bank-label { width: 80px; font-weight: bold; }
            .bank-value { flex: 1; }
            .summary-section { margin-top: 20px; }
            .summary-row { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 12px; }
            .summary-label { font-weight: bold; }
            .summary-value { text-align: right; }
            .amount-words { margin-top: 10px; font-size: 12px; font-style: italic; }
            .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
            .signature-box { width: 45%; text-align: center; }
            .signature-line { border-bottom: 1px solid #000; margin-bottom: 5px; height: 40px; }
            .signature-text { font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <div class="company-name">SHREENATH NX</div>
            <div class="company-subtitle">GST: 24AAUPP5605D1ZV | PAN: AAUPP5605D | MOB: 99244 63040</div>
          </div>

          <div class="invoice-title">TAX INVOICE</div>

          <div class="invoice-details">
            <div class="detail-col">
              <div class="detail-row">
                <span class="detail-label">Invoice No.:</span>
                <span class="detail-value">WT/0001</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Invoice Date:</span>
                <span class="detail-value">${escapeHtml(orderDate)}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Date Of Supply:</span>
                <span class="detail-value">${escapeHtml(orderDate)}</span>
              </div>
            </div>
            <div class="detail-col">
              <div class="detail-row">
                <span class="detail-label">Place Of Supply:</span>
                <span class="detail-value">GUJARAT</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Order No.:</span>
                <span class="detail-value">#${escapeHtml(orderNumber)}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Dispatched No:</span>
                <span class="detail-value">N/A</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">SalesMan:</span>
                <span class="detail-value">N/A</span>
              </div>
            </div>
          </div>

          <div class="address-section">
            <div class="address-col">
              <div class="address-title">Bill To</div>
              <div class="address-content">
                <div><strong>${escapeHtml(customerName)}</strong></div>
                <div>${escapeHtml(order.address?.addressLine || parent?.savedAddress || "N/A")}</div>
                <div>${escapeHtml(order.address?.city || "")}, ${escapeHtml(order.address?.pincode || "")}</div>
                <div>Phone: ${escapeHtml(customerPhone)}</div>
                <div>GST No.: N/A</div>
                <div>State: GUJARAT</div>
                <div>Code: 24</div>
              </div>
            </div>
            <div class="address-col">
              <div class="address-title">Ship To</div>
              <div class="address-content">
                <div><strong>${escapeHtml(customerName)}</strong></div>
                <div>${escapeHtml(order.address?.addressLine || parent?.savedAddress || "N/A")}</div>
                <div>${escapeHtml(order.address?.city || "")}, ${escapeHtml(order.address?.pincode || "")}</div>
                <div>Phone: ${escapeHtml(customerPhone)}</div>
                <div>GST No.: N/A</div>
                <div>State: GUJARAT</div>
                <div>Code: 24</div>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Particulars</th>
                <th>HSN</th>
                <th>Size</th>
                <th>Qty</th>
                <th>UOM</th>
                <th>Rate</th>
                <th>Discount</th>
                <th>Net Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${(order.items || []).map((i) => `
                <tr>
                  <td class="text-left">${escapeHtml(i.name || i.description || "Uniform Item")}</td>
                  <td>6109</td>
                  <td>${escapeHtml(i.size || "N/A")}</td>
                  <td>${Number(i.quantity || 0)}</td>
                  <td>NOS</td>
                  <td class="text-right">&#8377;${formatAmount(i.price)}</td>
                  <td>0.00</td>
                  <td class="text-right">&#8377;${formatAmount(i.price)}</td>
                  <td class="text-right">&#8377;${formatAmount(Number(i.price || 0) * Number(i.quantity || 0))}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>

          <div class="bank-details">
            <div class="bank-title">Bank Details</div>
            <div class="bank-row">
              <span class="bank-label">Bank Name:</span>
              <span class="bank-value">THE KARAD URBAN CO-OP. BANK LTD.</span>
            </div>
            <div class="bank-row">
              <span class="bank-label">A/C Name:</span>
              <span class="bank-value">SHREENATH NX</span>
            </div>
            <div class="bank-row">
              <span class="bank-label">A/C No.:</span>
              <span class="bank-value">501200010965922</span>
            </div>
            <div class="bank-row">
              <span class="bank-label">Branch:</span>
              <span class="bank-value">KARAD</span>
            </div>
            <div class="bank-row">
              <span class="bank-label">IFSC Code:</span>
              <span class="bank-value">KARB0000001</span>
            </div>
          </div>

          <div class="summary-section">
            <div class="summary-row">
              <span class="summary-label">Taxable Amount:</span>
              <span class="summary-value">&#8377;${formatAmount(taxableAmount)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Discount Amount:</span>
              <span class="summary-value">&#8377;0.00</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">All Taxes:</span>
              <span class="summary-value">Included</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Additional Amount:</span>
              <span class="summary-value">&#8377;0.00</span>
            </div>
            <div class="summary-row" style="border-top: 1px solid #000; padding-top: 5px; font-weight: bold;">
              <span class="summary-label">Net Amount:</span>
              <span class="summary-value">&#8377;${formatAmount(netAmount)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Received Amount:</span>
              <span class="summary-value">&#8377;${formatAmount(netAmount)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Outstanding Amount:</span>
              <span class="summary-value">&#8377;0.00</span>
            </div>
          </div>

          <div class="amount-words">
            Rupees ${escapeHtml(numberToWords(totalAmount))}
          </div>

          <div class="signatures">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-text">Customer Signature</div>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-text">For SHREENATH NX</div>
            </div>
          </div>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  useEffect(() => {
    fetchData();
  }, [parentId]);

  const fetchData = async () => {
  setLoading(true);
  try {
    const parentRes = await axios.get(`${API_BASE_URL}/parent/parent/${parentId}`);
    setParent(parentRes.data);

    const orderRes = await axios.get(`${API_BASE_URL}/orders/parent/${parentId}`);
    setOrders(orderRes.data);
    
    console.log("Parent Data:", parentRes.data);
    console.log("Orders Data:", orderRes.data);
  } catch (err) {
    console.error("Error fetching details:", err.response?.data || err.message);
  } finally {
    setLoading(false);
  }
};

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{height: "100vh"}}>
      <div className="spinner-grow text-success" role="status"></div>
    </div>
  );

  return (
    <div className="p-4" style={{ backgroundColor: "#f4f7fe", minHeight: "100vh", fontFamily: "'Poppins', sans-serif" }}>
      
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <button
            className="btn btn-md d-flex align-items-center gap-2 fw-bold text-muted border-0 bg-transparent"
            onClick={() => navigate("/dashboard/parents")}>
            <FaArrowLeft /> Back
          </button>
      </div>

      <div className="row g-4">
        {/* 🟢 LEFT SIDE: PROFILE CARD (Status is View-Only here) */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm text-center p-4 h-100" style={{ borderRadius: "24px", background: "linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)" }}>
            <div className="position-relative mb-3 d-inline-block mx-auto">
              <div style={{ width: "100px", height: "100px", backgroundColor: "#34a87b", borderRadius: "30px", transform: "rotate(-10deg)" }} className="position-absolute opacity-10"></div>
              <div style={{ width: "100px", height: "100px", backgroundColor: "#fff", borderRadius: "30px", boxShadow: "0 10px 20px rgba(0,0,0,0.05)" }} className="d-flex align-items-center justify-content-center text-success position-relative">
                <FaUser size={40} />
              </div>
            </div>
            
            <h4 className="fw-bold text-dark mb-1">{parent?.name || "User Name"}</h4>
            <p className="text-muted small mb-3">{parent?.email}</p>
            
            {/* 🛡️ Status Display Badge (Read Only) */}
            <div className="d-flex justify-content-center mb-4">
               <span className="badge d-flex align-items-center gap-2 px-4 py-2 shadow-sm" 
                     style={{ 
                       backgroundColor: parent?.active !== false ? "#34a87b" : "#dc3545", 
                       color: "white",
                       borderRadius: "30px",
                       fontSize: "12px",
                       letterSpacing: "0.5px"
                     }}>
                 {parent?.active !== false ? (
                   <><FaCheckCircle /> ACTIVE ACCOUNT</>
                 ) : (
                   <><FaTimesCircle /> INACTIVE ACCOUNT</>
                 )}
               </span>
            </div>

            <div className="text-start">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="p-2 rounded-3" style={{backgroundColor: "#edf2ff", color: "#4c6ef5"}}><FaEnvelope /></div>
                <div>
                  <small className="text-muted d-block" style={{fontSize: "11px"}}>Email Address</small>
                  <span className="fw-bold small">{parent?.email || "-"}</span>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="p-2 rounded-3" style={{backgroundColor: "#e3fafc", color: "#1098ad"}}><FaPhoneAlt /></div>
                <div>
                  <small className="text-muted d-block" style={{fontSize: "11px"}}>Mobile Number</small>
                  <span className="fw-bold small">{parent?.mobile || "-"}</span>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="p-2 rounded-3" style={{backgroundColor: "#fff4e6", color: "#fd7e14"}}><FaSchool /></div>
                <div>
                  <small className="text-muted d-block" style={{fontSize: "11px"}}>Mapped School</small>
                  <span className="fw-bold small">{parent?.schoolId?.name || "Not Mapped"} ({parent?.schoolId?.schoolCode})</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🟢 RIGHT SIDE: ORDER TIMELINE */}
        <div className="col-lg-8">
          <div className="d-flex align-items-center gap-2 mb-3">
             
             <h5 className="fw-bold m-0 text-dark">Order Details</h5>
          </div>

          <div className="order-scroll-container" style={{ maxHeight: "75vh", overflowY: "auto", paddingRight: "10px" }}>
            {orders.length > 0 ? orders.map((order, idx) => (
              <div key={order._id} className="card border-0 shadow-sm mb-4" style={{ borderRadius: "20px", overflow: "hidden" }}>
                {/* Order Meta Header */}
                <div className="p-3 d-flex justify-content-between align-items-center" style={{backgroundColor: "#f8f9fa"}}>
                  <div className="d-flex align-items-center gap-3">
                    <span className="fw-bold text-success">#{orders.length - idx}</span>
                    <div className="vr text-muted opacity-25" style={{height: "20px"}}></div>
                    <span className="text-muted small fw-bold">ID: {order._id.slice(-6).toUpperCase()}</span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-light border shadow-sm d-flex align-items-center gap-2 fw-bold"
                      onClick={() => handleInvoice(order)}
                      style={{ borderRadius: "10px", color: "#097969" }}
                    >
                      <FaFileInvoice /> Invoice
                    </button>
                    <div className="d-flex align-items-center gap-2">
                      <FaCalendarAlt className="text-muted small" />
                      <span className="small fw-bold text-muted">{new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>

                <div className="card-body p-4">
                  <div className="row g-3">
                    {order.items.map((item, i) => (
                      <div key={i} className="col-md-6">
                        <div className="d-flex align-items-center p-3" style={{ borderRadius: "16px", border: "1px solid #f1f3f5", backgroundColor: "#fff" }}>
                          <img 
                            src={item.uniformId?.image ? `${API_BASE_URL}/uploads/${item.uniformId.image}` : "https://via.placeholder.com/80"} 
                            alt="product"
                            width="60" height="60" 
                            style={{ objectFit: "cover", borderRadius: "12px", boxShadow: "0 4px 10px rgba(0,0,0,0.05)" }} 
                            className="me-3" 
                          />
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold text-dark small">{item.name}</h6>
                            <div className="d-flex justify-content-between align-items-center">
                               <span className="text-muted" style={{fontSize: "12px"}}>₹{item.price} × {item.quantity}</span>
                               <span className="fw-bold text-dark small">₹{item.price * item.quantity}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Footer Total */}
                  <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-end">
                    <div>
                      <small className="text-muted d-block">Items Ordered</small>
                      <span className="fw-bold text-dark">{order.items.length} Product(s)</span>
                    </div>
                    <div className="text-end">
                      <small className="text-muted d-block">Total Amount Paid</small>
                      <h4 className="fw-bold text-success mb-0">₹{order.totalAmount}</h4>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center p-5 card border-0 shadow-sm" style={{borderRadius: "24px"}}>
                <FaShoppingBag size={50} className="text-muted mb-3 opacity-25 mx-auto"/>
                <h5 className="text-muted">No orders placed yet</h5>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .order-scroll-container::-webkit-scrollbar { width: 6px; }
        .order-scroll-container::-webkit-scrollbar-track { background: transparent; }
        .order-scroll-container::-webkit-scrollbar-thumb { background: #d1d1d1; border-radius: 10px; }
        
        .btn-white { background: white; border: 1px solid #eee; }
        .btn-white:hover { transform: translateY(-2px); box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important; }
        
        .card { transition: 0.3s; }
        .card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.05) !important; }
      `}</style>
    </div>
  );
}

export default ParentOrderDetails;
